import moment from "moment";

// Formatta una data in "DD/MM/yyyy"
export function formatDate(date: Date | string | null | undefined): string {
    if (date) {
        return moment(new Date(date)).format("DD/MM/yyyy");
    }
    return "";
}

// Formatta una data in "DD/MM/yyyy HH:mm"
export function formatDateToMin(date: Date | string | null | undefined): string {
    if (date) {
        return moment(new Date(date)).format("DD/MM/yyyy HH:mm");
    }
    return "";
}

// Verifica se il range di date include l'ora corrente
export function isCurrentDateTimeSpan(from: Date, to: Date): boolean {
    const nowFrom = new Date(new Date().setHours(0, 0, 0, 0));
    const nowTo = new Date(new Date().setHours(23, 59, 59, 999));

    return from.getTime() === nowFrom.getTime() && to.getTime() === nowTo.getTime();
}

// Ottieni l'intervallo di tempo del giorno corrente
export function getCurrentDateTimeSpan(): { from: Date; to: Date } {
    const from = new Date(new Date().setHours(0, 0, 0, 0));
    const to = new Date(new Date().setHours(23, 59, 59, 999));

    return { from, to };
}

// Ottieni l'intervallo di tempo dell'ora corrente
export function getCurrentHourTimeSpan(): { from: Date; to: Date } {
    const from = new Date(new Date().setHours(new Date().getHours(), 0, 0, 0));
    const to = new Date(new Date().setHours(new Date().getHours(), 59, 59, 999));

    return { from, to };
}

// Ottieni l'orario da una data in "HH:mm:ss"
export function getTimeFromDate(date: Date | string | null | undefined): string {
    if (date) {
        return moment(date).format("HH:mm:ss");
    }
    return "";
}

// Converte una data in formato UTC
export function toUTC(date: Date | string): string {
    return moment.utc(date).format("YYYY-MM-DDTHH:mm:ss");
}

// Formatta una data senza fuso orario in "DD/MM/yyyy"
export function formatDateNoTZ(date: Date | string | null | undefined): string {
    if (date) {
        const dateStr = typeof date === "string" ? date : date.toISOString();
        return moment(removeTZ(dateStr)).format("DD/MM/yyyy");
    }
    return "";
}

// Formatta una data senza fuso orario in "DD/MM/yyyy HH:mm"
export function formatDateToMinNoTZ(date: Date | string | null | undefined): string {
    if (date) {
        const dateStr = typeof date === "string" ? date : date.toISOString();
        return moment(removeTZ(dateStr)).format("DD/MM/yyyy HH:mm");
    }
    return "";
}

// Ottieni l'orario da una data senza fuso orario in "HH:mm"
export function getTimeFromDateNoTZ(date: Date | string | null | undefined): string {
    if (date) {
        const dateStr = typeof date === "string" ? date : date.toISOString();
        return moment(removeTZ(dateStr)).format("HH:mm");
    }
    return "";
}

// Rimuove il fuso orario da una stringa di data
export function removeTZ(dateStr: string | Date): string {
    const str = typeof dateStr === "string" ? dateStr : dateStr.toISOString();
    if (str && str.length > 0 && str.indexOf("+") >= 0) {
        return str.replace(str.substring(str.indexOf("+")), "");
    }
    return str;
}

// Converte un valore in oggetto Date
export function toDateObject(date: Date | string): Date {
    return date instanceof Date ? date : new Date(date);
}

// Calcola la differenza temporale e restituisce la durata
export function calculateTimeDifferenceAndFormat(
    firstDate: Date | string,
    secondDate: Date | string
): moment.Duration {
    const firstMoment = moment(toDateObject(firstDate));
    const secondMoment = moment(toDateObject(secondDate));
    return moment.duration(secondMoment.diff(firstMoment));
}

// Verifica se un valore è una data valida
export function isValidDate(date: unknown): boolean {
    return date instanceof Date && !isNaN(date.valueOf());
}

const expectedDate = (earlyDate: Date | string, lateDate: Date | string) => {
    const early = moment(earlyDate);
    const late = moment(lateDate);
    let qualifier = "";
    let expectedDate: any[] = [];
    let qualifierStyle = {};

    if (early.isValid() && !late.isValid())
        qualifier = "later than";
    else if (!early.isValid() && late.isValid())
        qualifier = "within";
    else if (early.isValid() && late.isValid() && early.isSame(late)) {
        qualifier = "at";
        qualifierStyle = { color: 'red', fontWeight: 'bold' };
    } else if (early.isValid() && late.isValid() && !early.isSame(late))
        if (early.isAfter(late))
            qualifier = "";
        else
            qualifier = "between";

    if (qualifier === "later than" || qualifier === "at")
        expectedDate = [`${early.format("DD/MM/yyyy")}`, qualifier, `${early.format("HH:mm")}`];
    else if (qualifier === "within")
        expectedDate = [`${late.format("DD/MM/yyyy")}`, qualifier, `${late.format("HH:mm")}`];
    else if (qualifier === "between")
        expectedDate = [`${early.format("DD/MM/yyyy")}`, qualifier, `${early.format("HH:mm")}`, "-", `${late.format("HH:mm")}`];
    else if (qualifier === "")
        expectedDate = [`${early.format("DD/MM/yyyy")}`];

    return (
        <div>
            {expectedDate.map((word, index) => word === 'at' ?
                <span key={index} style={qualifierStyle}>{word} </span> : word + ' ')}
        </div>);
};

export const formatDateTime = (isoString?: string): string => {
    if (!isoString) return "-"; // Se undefined o null, ritorna "-"
    
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "-"; // Se la data non è valida, ritorna "-"
  
    const pad = (num: number) => num.toString().padStart(2, "0");
  
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
  
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };
  

  export const formatDateGGMMAAAA = (isoString: string): string => {
    const date = new Date(isoString);
  
    const pad = (num: number) => num.toString().padStart(2, "0");
  
    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
 
  
    return `${day}/${month}/${year}`;
  };

  export const formatDateIgnoreTimezone = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null;
  
    const [datePart, timePart] = dateString.split("T"); // Separiamo data e ora
    if (!datePart || !timePart) return null;
  
    const [year, month, day] = datePart.split("-"); // Estraggo anno, mese e giorno
    const [hours, minutes] = timePart.split(":"); // Estraggo ore e minuti
  
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };
  

  

  export const formatMonthYear = (isoString: string): string => {
    const date = new Date(isoString);
  
    const pad = (num: number) => num.toString().padStart(2, "0");
  
    const month = pad(date.getMonth() + 1);
    const year = date.getFullYear();
  
    return `${month}/${year}`;
  };
  